
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  BadgeCheck,
  Cable,
  Eye,
  EyeOff,
  FlaskConical,
  History,
  KeyRound,
  Link,
  RefreshCcw,
  RefreshCw,
  Trash2,
} from 'lucide-react'
import { useAuthSession } from '../../auth/session'
import {
  fetchBusinessIacrmSettings,
  fetchOwnIacrmSettings,
  testBusinessIacrmConnection,
  testOwnIacrmConnection,
  updateBusinessIacrmSettings,
  updateOwnIacrmSettings,
} from '../api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import { getAppLocale } from '@/lib/locale'
import { toast } from 'sonner'
import {
  hasIacrmConfig,
  saveIacrmConfig,
} from '../../iacrm/api'
import type { IacrmApiConfig } from '../../../types/iacrm'
import { SettingsGroup } from './SettingsGroup'
import { SettingsRow } from './SettingsRow'
import { SyncIncidentsTable } from './SyncIncidentsTable'

type ApiTone = 'neutral' | 'active' | 'success' | 'warning' | 'danger'

function apiToneBadgeClass(tone: ApiTone) {
  switch (tone) {
    case 'active':
      return 'border-transparent bg-sky-500/15 text-sky-900 dark:bg-sky-500/20 dark:text-sky-300'
    case 'success':
      return 'border-transparent bg-emerald-500/15 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300'
    case 'warning':
      return 'border-transparent bg-amber-500/15 text-amber-900 dark:bg-amber-500/20 dark:text-amber-300'
    case 'danger':
      return 'border-transparent bg-rose-500/15 text-rose-800 dark:bg-rose-500/20 dark:text-rose-300'
    default:
      return 'border-transparent bg-muted text-muted-foreground'
  }
}

const businessIacrmQueryKey = ['settings', 'business', 'iacrm']
const ownIacrmQueryKey = ['settings', 'own', 'iacrm']

export function IacrmApiSettingsTab({
  failedJobsValue,
}: {
  failedJobsValue: string
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { user } = useAuthSession()
  const canManageBusinessIacrm =
    Boolean(user?.current_business_id) &&
    (user?.permissions.includes('settings.view-business') || user?.permissions.includes('settings.update-business'))
  const canViewSync = user?.permissions.includes('iacrm.sync-view') ?? false

  const businessConfigQuery = useQuery({
    queryKey: [...businessIacrmQueryKey, user?.current_business_id],
    queryFn: fetchBusinessIacrmSettings,
    enabled: canManageBusinessIacrm,
  })

  const ownConfigQuery = useQuery({
    queryKey: ownIacrmQueryKey,
    queryFn: fetchOwnIacrmSettings,
    enabled: !canManageBusinessIacrm,
  })

  const existingConfig = useMemo(() => {
    if (canManageBusinessIacrm) return businessConfigQuery.data?.data ?? null
    return ownConfigQuery.data?.data ?? null
  }, [canManageBusinessIacrm, businessConfigQuery.data?.data, ownConfigQuery.data?.data])
  const isPlatformConfig =
    !canManageBusinessIacrm &&
    Boolean(user?.permissions.includes('settings.view-platform'))

  const [baseUrl, setBaseUrl] = useState(existingConfig?.base_url ?? '')
  const [apiKey, setApiKey] = useState(existingConfig?.api_key ?? '')
  const [showApiKey, setShowApiKey] = useState(false)

  useEffect(() => {
    setBaseUrl(existingConfig?.base_url ?? '')
    setApiKey(existingConfig?.api_key ?? '')
  }, [existingConfig?.base_url, existingConfig?.api_key])

  const saveMutation = useMutation({
    mutationFn: async (config: IacrmApiConfig) => {
      if (canManageBusinessIacrm) {
        return updateBusinessIacrmSettings({
          base_url: config.base_url,
          api_key: config.api_key,
          auto_sync_enabled: true,
        })
      }

      return updateOwnIacrmSettings({
        base_url: config.base_url,
        api_key: config.api_key,
        auto_sync_enabled: true,
      })
    },
    onSuccess: (response) => {
      if (canManageBusinessIacrm) {
        queryClient.setQueryData([...businessIacrmQueryKey, user?.current_business_id], response)
      } else {
        queryClient.setQueryData(ownIacrmQueryKey, response)
      }
      saveIacrmConfig(response.data)
      void queryClient.refetchQueries({ queryKey: ['dashboard', 'business-summary'] })
      toast.success(t('settings.api.toasts.configSaved'), { id: 'settings-api-toast' })
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : t('settings.api.toasts.configSaveFailed'), {
        id: 'settings-api-toast',
      })
    },
  })

  const testMutation = useMutation({
    mutationFn: async (config: IacrmApiConfig) => {
      if (canManageBusinessIacrm) {
        return testBusinessIacrmConnection({
          base_url: config.base_url,
          api_key: config.api_key,
          auto_sync_enabled: true,
        })
      }

      return testOwnIacrmConnection({
        base_url: config.base_url,
        api_key: config.api_key,
        auto_sync_enabled: true,
      })
    },
    onSuccess: (response) => {
      if (canManageBusinessIacrm) {
        queryClient.setQueryData([...businessIacrmQueryKey, user?.current_business_id], { data: response.data })
      } else {
        queryClient.setQueryData(ownIacrmQueryKey, { data: response.data })
      }
      saveIacrmConfig(response.data)

      if (response.meta.ok) {
        toast.success(t('settings.api.toasts.connectionValid'), { id: 'settings-api-toast' })
        return
      }

      toast.error(t('settings.api.toasts.connectionFailed', { message: response.meta.message }), { id: 'settings-api-toast' })
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : t('settings.api.toasts.connectionTestFailed'), {
        id: 'settings-api-toast',
      })
    },
  })

  const currentConfig = (): IacrmApiConfig => ({
    base_url: baseUrl.trim(),
    api_key: apiKey.trim(),
    auto_sync_enabled: true,
    last_tested_at: existingConfig?.last_tested_at ?? null,
    connection_status: existingConfig?.connection_status ?? 'untested',
  })

  const connectionStatus = existingConfig?.connection_status ?? 'untested'
  const lastTested = existingConfig?.last_tested_at
    ? new Date(existingConfig.last_tested_at).toLocaleString(getAppLocale(), {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : t('settings.api.never')

  const failedJobsCount = Number.parseInt(failedJobsValue, 10) || 0
  const statusLabel =
    connectionStatus === 'connected'
      ? t('settings.api.status.connected')
      : connectionStatus === 'failed'
        ? t('settings.api.status.failed')
        : t('settings.api.status.untested')

  const existingBaseUrl = existingConfig?.base_url ?? ''
  const existingApiKey = existingConfig?.api_key ?? ''
  const hasConfigChanges = baseUrl.trim() !== existingBaseUrl || apiKey.trim() !== existingApiKey
  const loadingRemoteConfig =
    (canManageBusinessIacrm && businessConfigQuery.isPending) ||
    (!canManageBusinessIacrm && ownConfigQuery.isPending)
  const saveDisabledReason = loadingRemoteConfig
    ? t('settings.api.loadingBusinessConfig')
    : !hasConfigChanges
      ? existingApiKey
        ? t('settings.api.noChangesWithKey')
        : t('settings.api.noChanges')
      : null

  function handleSave() {
    saveMutation.mutate(currentConfig())
  }

  function handleTest() {
    testMutation.mutate(currentConfig())
  }

  function handleClearKey() {
    setApiKey('')
    toast.warning(
      canManageBusinessIacrm
        ? t('settings.api.toasts.keyRemovedBusiness')
        : t('settings.api.toasts.keyRemovedLocal'),
      { id: 'settings-api-toast' },
    )
  }

  function handleResetConfig() {
    setBaseUrl(existingConfig?.base_url ?? '')
    setApiKey(existingConfig?.api_key ?? '')
    setShowApiKey(false)
    toast.warning(
      canManageBusinessIacrm
        ? t('settings.api.toasts.draftResetBackend')
        : t('settings.api.toasts.configResetLocal'),
      { id: 'settings-api-toast' },
    )
  }

  return (
    <div className="space-y-6">
      <SettingsGroup label={t('settings.api.title')}>
        <div className="bg-card px-4 py-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground" htmlFor="iacrm-base-url">
                {isPlatformConfig ? t('settings.api.platformEndpointLabel') : t('settings.api.serverUrlLabel')}
              </label>
              <div className="relative">
                <Link className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="iacrm-base-url"
                  type="url"
                  value={baseUrl}
                  onChange={(event) => setBaseUrl(event.target.value)}
                  placeholder={
                    isPlatformConfig
                      ? t('settings.api.platformEndpointPlaceholder')
                      : t('settings.api.serverUrlPlaceholder')
                  }
                  className="pl-9 font-mono text-sm"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {isPlatformConfig
                  ? t('settings.api.platformEndpointHint')
                  : canManageBusinessIacrm
                    ? t('settings.api.businessEndpointHint')
                    : t('settings.api.sourceLabel', {
                        source: t('settings.api.sourceLocal'),
                      })}
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground" htmlFor="iacrm-api-key">
                {t('settings.api.apiKey')}
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <KeyRound className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="iacrm-api-key"
                    type={showApiKey ? 'text' : 'password'}
                    value={apiKey}
                    onChange={(event) => setApiKey(event.target.value)}
                    placeholder="••••••••••••••••"
                    className="pl-9"
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="gap-2"
                  onClick={() => setShowApiKey((current) => !current)}
                >
                  {showApiKey ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  <span className="hidden sm:inline">{showApiKey ? t('settings.api.hide') : t('settings.api.show')}</span>
                </Button>
              </div>
            </div>

            <Separator className="bg-border/50" />

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                className="gap-2"
                data-onboarding-target="iacrm"
                disabled={Boolean(saveDisabledReason) || saveMutation.isPending}
                onClick={handleSave}
              >
                <BadgeCheck className="size-4" />
                {saveMutation.isPending ? t('settings.api.saving') : t('settings.api.saveConfig')}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="gap-2"
                disabled={testMutation.isPending || apiKey.trim().length === 0 || loadingRemoteConfig}
                onClick={handleTest}
              >
                <FlaskConical className="size-4" />
                {testMutation.isPending ? t('settings.api.testing') : t('settings.api.testConnection')}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="gap-2"
                onClick={handleClearKey}
                disabled={apiKey.length === 0}
              >
                <Trash2 className="size-4" />
                {t('settings.api.clearKey')}
              </Button>
              <Button type="button" variant="outline" className="gap-2" onClick={handleResetConfig}>
                <RefreshCcw className="size-4" />
                {t('settings.api.resetLocal')}
              </Button>
            </div>

            {saveDisabledReason ? <p className="text-sm text-muted-foreground">{saveDisabledReason}</p> : null}

            {!hasIacrmConfig(existingConfig) ? (
              <p className="text-sm text-muted-foreground">{t('settings.api.noKeyWarning')}</p>
            ) : null}
          </div>
        </div>
      </SettingsGroup>

      <SettingsGroup label={t('settings.api.connection')}>
        <SettingsRow
          icon={<Cable className="size-4" />}
          label={t('settings.api.connection')}
          value={statusLabel}
        />
        <SettingsRow
          icon={<BadgeCheck className="size-4" />}
          label={t('settings.api.lastTested')}
          value={lastTested}
        />
        <SettingsRow
          icon={<RefreshCw className="size-4" />}
          label={t('settings.api.syncMode')}
          value={t('settings.api.autoSyncEnabled')}
          badges={
            <Badge variant="outline" size="sm" className={cn('rounded-full', apiToneBadgeClass('success'))}>
              {t('common.active')}
            </Badge>
          }
        />
      </SettingsGroup>

      <SettingsGroup
        label={t('settings.sync.incidents')}
        action={
          canViewSync ? (
            <Badge variant="outline" size="sm">
              {failedJobsCount > 0
                ? t('settings.sync.activeIssues', { count: failedJobsCount })
                : t('settings.sync.healthy')}
            </Badge>
          ) : null
        }
      >
        {canViewSync ? (
          <SyncIncidentsTable />
        ) : (
          <div className="flex items-center gap-3.5 bg-card px-4 py-5">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted/40 text-muted-foreground">
              <History className="size-4" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">{t('settings.sync.noVisibility')}</p>
            </div>
          </div>
        )}
      </SettingsGroup>
    </div>
  )
}
